<script lang="ts">
  import PasswordInput from '$shared/PasswordInput.svelte';
  import {
    login,
    getUser,
    startTikTokLogin,
    startGoogleLogin,
    startSpotifyLogin,
    startAppleLogin,
  } from '$lib/auth.svelte';
  import { navigate } from '$lib/router.svelte';
  import { banReasonCopy } from '$lib/banReason';
  import { trackEvent } from '$lib/analytics.svelte';
  import { consumeReturnTo } from '$lib/utils/returnTo';

  let username = $state('');
  let password = $state('');
  let error = $state('');
  let banReason = $state<string | null>(null);
  let loading = $state(false);
  let tiktokLoading = $state(false);
  let googleLoading = $state(false);
  let spotifyLoading = $state(false);
  let appleLoading = $state(false);

  // Check for OAuth errors from redirect (TikTok, Google, Spotify, and Apple share this UI)
  const tiktokError = sessionStorage.getItem('tiktok_login_error');
  if (tiktokError) {
    error = tiktokError;
    sessionStorage.removeItem('tiktok_login_error');
  }
  const googleError = sessionStorage.getItem('google_login_error');
  if (googleError) {
    error = googleError;
    sessionStorage.removeItem('google_login_error');
  }
  const spotifyError = sessionStorage.getItem('spotify_login_error');
  if (spotifyError) {
    error = spotifyError;
    sessionStorage.removeItem('spotify_login_error');
  }
  const appleError = sessionStorage.getItem('apple_login_error');
  if (appleError) {
    error = appleError;
    sessionStorage.removeItem('apple_login_error');
  }

  async function handleLogin(e: Event) {
    e.preventDefault();
    error = '';
    banReason = null;
    loading = true;
    try {
      await login(username, password);
      trackEvent('login', { metadata: { method: 'password' } });
      // An existing user who never finished onboarding still needs to go through
      // it first (the App.svelte guard would redirect them there anyway) — leave
      // the stashed return-to in place so Onboarding's completion step can honor
      // it instead of consuming (and losing) it here.
      if (getUser()?.onboarding_complete === false) {
        navigate('/onboarding');
      } else {
        navigate(consumeReturnTo() || '/duels');
      }
    } catch (err) {
      const reason = (err as Error & { banReason?: string }).banReason;
      if (reason !== undefined) {
        banReason = reason;
      } else {
        error = (err as Error).message || 'Login failed';
      }
    } finally {
      loading = false;
    }
  }

  async function handleTikTokLogin() {
    tiktokLoading = true;
    error = '';
    try {
      const url = await startTikTokLogin();
      window.location.href = url;
    } catch (err) {
      error = (err as Error).message || 'Could not connect to TikTok';
      tiktokLoading = false;
    }
  }

  async function handleGoogleLogin() {
    googleLoading = true;
    error = '';
    try {
      const url = await startGoogleLogin();
      window.location.href = url;
    } catch (err) {
      error = (err as Error).message || 'Could not connect to Google';
      googleLoading = false;
    }
  }

  async function handleSpotifyLogin() {
    spotifyLoading = true;
    error = '';
    try {
      const url = await startSpotifyLogin();
      window.location.href = url;
    } catch (err) {
      error = (err as Error).message || 'Could not connect to Spotify';
      spotifyLoading = false;
    }
  }

  async function handleAppleLogin() {
    appleLoading = true;
    error = '';
    try {
      const url = await startAppleLogin();
      window.location.href = url;
    } catch (err) {
      error = (err as Error).message || 'Could not connect to Apple';
      appleLoading = false;
    }
  }
</script>

<div class="login-page">
  {#if banReason !== null}
    <div class="auth-card banned-card">
      <h2>Account Restricted</h2>
      <p class="banned-lead">Your account has been restricted and cannot sign in.</p>
      {#if banReason}
        <p class="banned-reason"><strong>Reason:</strong> {banReasonCopy(banReason)}</p>
      {/if}
      <p class="banned-support">
        If you think this is a mistake, contact
        <a href="mailto:support@beatduel.com">support@beatduel.com</a>.
      </p>
      <button class="btn btn-ghost full-width" type="button" onclick={() => (banReason = null)}>
        Back to sign in
      </button>
    </div>
  {:else}
    <form class="auth-card" onsubmit={handleLogin}>
      <h2>Sign In</h2>
      {#if error}<p class="msg--error">{error}</p>{/if}
      <label class="field"
        ><span class="field-label">Username</span>
        <input class="input" type="text" bind:value={username} required autocomplete="username" /></label
      >
      <PasswordInput
        id="login-password"
        label="Password"
        bind:value={password}
        required
        autocomplete="current-password"
      />
      <div class="forgot-link"><a href="#/forgot">Forgot your password?</a></div>
      <button class="btn btn-primary full-width" type="submit" disabled={loading}>
        {loading ? 'Signing in...' : 'Sign In'}
      </button>
      <div class="social-divider"><span>or</span></div>
      <button
        class="btn btn-social btn-social--apple full-width"
        type="button"
        onclick={handleAppleLogin}
        disabled={appleLoading}
      >
        <svg class="social-icon" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
          <path
            d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282z"
          />
        </svg>
        {appleLoading ? 'Redirecting...' : 'Continue with Apple'}
      </button>
      <button
        class="btn btn-social btn-social--google full-width"
        type="button"
        onclick={handleGoogleLogin}
        disabled={googleLoading}
      >
        <svg class="social-icon" viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
          />
          <path
            fill="#FBBC05"
            d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
          />
        </svg>
        {googleLoading ? 'Redirecting...' : 'Continue with Google'}
      </button>
      <button
        class="btn btn-social btn-social--tiktok full-width"
        type="button"
        onclick={handleTikTokLogin}
        disabled={tiktokLoading}
      >
        {tiktokLoading ? 'Redirecting...' : 'Continue with TikTok'}
      </button>
      <button
        class="btn btn-social btn-social--spotify full-width"
        type="button"
        onclick={handleSpotifyLogin}
        disabled={spotifyLoading}
      >
        {spotifyLoading ? 'Redirecting...' : 'Continue with Spotify'}
      </button>
    </form>

    <p class="auth-link">Don't have an account? <a href="#/register">Sign Up</a></p>
  {/if}
</div>

<style>
  .banned-card {
    text-align: left;
  }
  .banned-lead {
    color: #e0e0e0;
    font-size: 15px;
    margin: 8px 0 12px;
  }
  .banned-reason {
    background: rgba(239, 68, 68, 0.08);
    border-left: 3px solid #ef4444;
    padding: 10px 12px;
    color: #e0e0e0;
    border-radius: 3px;
    font-size: 14px;
  }
  .banned-support {
    color: #888;
    font-size: 13px;
    margin: 16px 0;
  }
  .login-page {
    max-width: 400px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .auth-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .auth-card h2 {
    text-align: center;
    color: var(--text);
    font-size: 1.1rem;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    margin: 0 0 0.5rem;
    -webkit-text-fill-color: var(--text);
    background: none;
    background-clip: unset;
    -webkit-background-clip: unset;
  }
  .field {
    display: block;
  }
  .field-label {
    display: block;
    font-size: 0.875rem;
    color: var(--text-muted);
    margin-bottom: 0.25rem;
  }
  .forgot-link {
    text-align: right;
  }
  .forgot-link a {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .forgot-link a:hover {
    color: var(--pink);
  }
  .full-width {
    width: 100%;
    padding: 0.625rem;
  }
  .input {
    max-width: 100%;
  }
  .social-divider {
    text-align: center;
    position: relative;
    margin: 0.25rem 0;
  }
  .social-divider span {
    background: var(--surface);
    padding: 0 0.75rem;
    color: var(--text-muted);
    font-size: 0.8rem;
    position: relative;
    z-index: 1;
  }
  .social-divider::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--border);
  }
  .btn-social {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    background: transparent;
    color: var(--text);
  }
  .btn-social:hover {
    opacity: 0.85;
  }
  .btn-social--tiktok {
    border: 1px solid #69c9d0;
  }
  .btn-social--google {
    border: 1px solid #4285f4;
  }
  .btn-social--spotify {
    background: #1db954;
    color: #fff;
    border: none;
  }
  .btn-social--spotify:hover:not(:disabled) {
    background: #1aa34a;
  }
  .btn-social--apple {
    background: #000;
    color: #fff;
    border: none;
  }
  .btn-social--apple:hover:not(:disabled) {
    background: #1a1a1a;
  }
  .social-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .auth-link {
    text-align: center;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
  .auth-link a {
    color: var(--cyan);
    text-decoration: underline;
  }
  .auth-link a:hover {
    text-decoration: none;
  }
</style>
